import { useCallback, useState } from 'react';

import Button from 'components/shared/Button';
import { useProfile } from 'context/profile';
import { PERMISSIONS } from 'models/flexible/Profile';
import { HighCostMedicationListImport } from 'services/api/admin/highCostMedicationListImports';
import PlusIcon from 'svg/PlusIcon';

import HighCostMedListImportTracker from './HighCostMedListImportTracker';
import UploadHighCostMedListModal from './UploadHighCostMedListModal';

/**
 * Owns the upload flow for the High Cost Meds tab: the entry-point button, the upload modal, and the
 * per-import processing/success/failure notifications (which outlive the modal). Lives in the tab's
 * headerExtra so the notifications persist after the modal closes. Leaving the tab unmounts the
 * trackers, so an import that finishes after that leaves its "processing" notification unresolved
 * and the tab's counts stale until the next refetch.
 *
 * View Only Olio Admins lack adminHighCostMedicationListCreate and never see the button or any
 * tracker, keeping the tab read-only for them.
 */
export default function UploadHighCostMedListButton() {
  const { profile } = useProfile();
  const [modalOpen, setModalOpen] = useState(false);
  const [trackedImports, setTrackedImports] = useState<HighCostMedicationListImport[]>([]);

  const handleUploaded = useCallback((created: HighCostMedicationListImport) => {
    setTrackedImports((prev) => [...prev, created]);
  }, []);

  const handleSettled = useCallback((importId: string) => {
    setTrackedImports((prev) => prev.filter((i) => i.id !== importId));
  }, []);

  if (!profile.has(PERMISSIONS.adminHighCostMedicationListCreate)) return null;

  return (
    <>
      <Button variant='primary' onClick={() => setModalOpen(true)}>
        <PlusIcon width={14} height={14} />
        Upload Med List
      </Button>

      {modalOpen && <UploadHighCostMedListModal onClose={() => setModalOpen(false)} onUploaded={handleUploaded} />}

      {trackedImports.map((imp) => (
        <HighCostMedListImportTracker key={imp.id} highCostMedicationListImport={imp} onSettled={handleSettled} />
      ))}
    </>
  );
}
